(() => {
  'use strict';
  const node = document.querySelector('[data-masi-commerce]');
  if (!node || window.MasiCart) return;
  const config = JSON.parse(node.textContent);
  const strings = JSON.parse(document.querySelector('[data-masi-commerce-strings]').textContent);
  config.strings = strings;
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const gifts = new Set(config.gifts.map(g => Number(g.id)));
  let queue = Promise.resolve(), busy = false;
  const api = { config, warning: '', uncertain: false, cart: null, isGift: item => gifts.has(Number(item.variant_id)),
    variant: id => Object.values(config.products).flatMap(p => p.variants).find(v => Number(v.id) === Number(id)),
    planFor: (variant,quantity) => variant?.plans?.find(p=>p.quantity===(Number(quantity)>=2?2:1)) || variant?.plan || null,
    money: cents => new Intl.NumberFormat(config.locale, {style:'currency',currency:api.cart?.currency || config.currency}).format(cents/100)
  };
  const managedLines = (cart,variant) => cart.items.filter(item => Number(item.variant_id) === Number(variant?.id)
    && variant?.plans?.some(plan => Number(plan.id) === Number(item.selling_plan_allocation?.selling_plan?.id)));
  api.displayItems = cart => {
    const items = [];
    for (const item of cart.items) {
      const variant = api.variant(item.variant_id);
      const managed = variant?.plans?.some(plan => Number(plan.id) === Number(item.selling_plan_allocation?.selling_plan?.id));
      const same = managed && items.find(line => line.masiSubscriptionGroup && line.variant_id === item.variant_id);
      if (same) {
        same.quantity += item.quantity;
        same.original_line_price += item.original_line_price;
        same.final_line_price += item.final_line_price;
      } else items.push({...item, masiSubscriptionGroup: !!managed});
    }
    return items;
  };
  // Gift lines never qualify as paid merchandise, even if a discount fails to apply.
  api.checkoutError = cart => {
    const paid = cart?.items?.some(item => !api.isGift(item) && item.quantity > 0 && item.final_line_price > 0);
    if (!paid || !Number.isFinite(cart?.total_price) || cart.total_price <= 0) {
      return cart?.items?.some(api.isGift) ? strings.gift_checkout_required : strings.paid_required;
    }
    return '';
  };
  async function request(path, data) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    let response, body;
    try {
      response = await fetch(root + path, {method:data ? 'POST':'GET', credentials:'same-origin', cache:'no-store', signal:controller.signal, headers:{Accept:'application/json', ...(data ? {'Content-Type':'application/json'}:{})}, ...(data ? {body:JSON.stringify(data)}:{})});
      body = await response.json();
    } catch (_) {
      if (data) { api.uncertain = true; api.warning = strings.uncertain; }
      throw new Error(data ? strings.uncertain : strings.error);
    } finally { clearTimeout(timeout); }
    if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : strings.error);
    return body;
  }
  const read = () => request('cart.js');
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, {detail}));
  async function reconcile(cart) {
    api.warning = '';
    // Shopify can split paid lines for Buy X Get Y. Select the tier from their combined quantity.
    for (const product of Object.values(config.products)) for (const variant of product.variants) {
      if (!variant.plans?.some(plan => plan.quantity === 2)) continue;
      const lines = managedLines(cart,variant);
      const quantity = lines.reduce((sum,item) => sum + item.quantity,0);
      if (!quantity) continue;
      const plan = api.planFor(variant,quantity);
      for (let remaining = lines.length; remaining > 0; remaining--) {
        const line = managedLines(cart,variant).find(item => Number(item.selling_plan_allocation.selling_plan.id) !== Number(plan.id));
        if (!line) break;
        cart = await request('cart/change.js',{line:cart.items.indexOf(line)+1,quantity:line.quantity,selling_plan:plan.id});
      }
    }
    const eligible = cart.items.some(item => !api.isGift(item) && item.selling_plan_allocation);
    const updates = {}, additions = [];
    for (const gift of config.gifts) {
      const lines = cart.items.filter(i => Number(i.variant_id) === Number(gift.id));
      const wanted = eligible && gift.available ? 1 : 0;
      lines.forEach((item,index) => {const qty = index === 0 ? wanted : 0; if(item.quantity !== qty) updates[item.key] = qty;});
      if (!lines.length && wanted) additions.push({id:gift.id,quantity:1,properties:{_masi_role:'welcome_gift'}});
      if(eligible && !gift.available) api.warning = strings.gift_error;
    }
    if (Object.keys(updates).length) cart = await request('cart/update.js',{updates});
    if (additions.length) {await request('cart/add.js',{items:additions}); cart = await read();}
    const charged = cart.items.filter(i => api.isGift(i) && i.final_line_price !== 0);
    if(charged.length) {
      cart = await request('cart/update.js',{updates:Object.fromEntries(charged.map(i=>[i.key,0]))});
      api.warning = strings.gift_error;
    }
    return cart;
  }
  function publish(cart) {
    api.cart = cart;
    const count = cart.items.filter(i => !api.isGift(i)).reduce((n,i)=>n+i.quantity,0);
    const icon=document.querySelector('#cart-icon-bubble');
    let bubble=icon?.querySelector('.ms-cart-count');
    if(icon&&!bubble){bubble=document.createElement('span');bubble.className='ms-cart-count';icon.append(bubble);}
    if(bubble){bubble.textContent=count;bubble.setAttribute('aria-label',String(count));bubble.hidden=!count;}
    emit('masi:cart:updated',cart);
    return cart;
  }
  function run(fn) {
    const result = queue.then(async()=>{
      busy = true;emit('masi:cart:busy',true);
      try { return publish(await fn()); }
      finally {busy=false;emit('masi:cart:busy',false);}
    });
    queue = result.catch(()=>{});
    return result;
  }
  api.refresh = () => run(async()=>{
    const cart = await read();
    const unqualifiedGifts = cart.items.some(api.isGift) && !!api.checkoutError(cart);
    const updated = api.uncertain ? cart : await reconcile(cart);
    if (unqualifiedGifts && api.checkoutError(updated) && !api.uncertain && !api.warning) api.warning = strings.gift_checkout_required;
    return updated;
  });
  api.add = item => run(async()=>{
    if(api.uncertain) throw new Error(strings.uncertain);
    if(api.isGift({variant_id:item.id})) throw new Error(strings.error);
    const variant=api.variant(item.id);
    if(item.selling_plan && variant?.plans?.some(p=>p.quantity===2) && variant.plans.some(p=>Number(p.id)===Number(item.selling_plan))) {
      const cart=await read();
      const quantity=Number(item.quantity)+managedLines(cart,variant).reduce((sum,line)=>sum+line.quantity,0);
      await request('cart/add.js',{items:[{...item,selling_plan:api.planFor(variant,quantity).id}]});
    } else await request('cart/add.js',{items:[item]});
    // The product is already added. A gift failure must not invite a second product submission.
    try { return await reconcile(await read()); }
    catch(error) {
      api.warning=api.uncertain?strings.uncertain:strings.gift_error;
      try {return await read();} catch (_) {api.uncertain=true;api.warning=strings.uncertain;throw new Error(strings.uncertain);}
    }
  });
  api.change = (key,quantity,plan) => run(async()=>{
    if(api.uncertain) throw new Error(strings.uncertain);
    let cart = await read();
    const index = cart.items.findIndex(i=>i.key===key), item=cart.items[index];
    if(!item || api.isGift(item)) throw new Error(strings.stale);
    const variant=api.variant(item.variant_id),plans=variant?.plans||[variant?.plan].filter(Boolean);
    const currentPlan=item.selling_plan_allocation?.selling_plan?.id;
    if(plan !== undefined && plan !== null && !plans.some(p=>Number(p.id)===Number(plan))) throw new Error(strings.stale);
    const managed=plans.some(p=>Number(p.id)===Number(currentPlan));
    if (managed) {
      const lines=managedLines(cart,variant);
      if (quantity !== undefined) {
        // Update split lines together so a quantity edit cannot remove units between separate requests.
        cart=await request('cart/update.js',{updates:Object.fromEntries(lines.map((line,i)=>[line.key,i===0?quantity:0]))});
      }
      if (plan === null) {
        for (let remaining=managedLines(cart,variant).length;remaining>0;remaining--) {
          const line=managedLines(cart,variant)[0];
          if (!line) break;
          cart=await request('cart/change.js',{line:cart.items.indexOf(line)+1,quantity:line.quantity,selling_plan:null});
        }
      }
    } else {
      const body={line:index+1,quantity:quantity ?? item.quantity};
      if (body.quantity>0 && plan) {
        const existing=managedLines(cart,variant).reduce((sum,line)=>sum+line.quantity,0);
        body.selling_plan=api.planFor(variant,body.quantity+existing).id;
      } else if (plan !== undefined) body.selling_plan=plan;
      cart=await request('cart/change.js',body);
    }
    try {return await reconcile(cart);}
    catch(error){
      api.warning=api.uncertain?strings.uncertain:strings.gift_error;
      try {return await read();} catch (_) {api.uncertain=true;api.warning=strings.uncertain;throw new Error(strings.uncertain);}
    }
  });
  api.isBusy=()=>busy;
  api.ready=queue;
  window.MasiCart=api;
})();
